import {
	BrokerService,
	TenantJobSearchFilters,
	TenantJobSearchStatus,
} from '@warehouse/object-browser/feature-broker-core';
import { SERVICE_LOG_STATUS_QUICK_FILTERS } from './service-log.model';

/**
 * Turns the search box text into Broker search filters.
 *
 * The Broker only ANDs conditions and has no "any field" search, so one request cannot OR a text across
 * fields. The text is therefore matched against what the page knows, most specific first:
 * 1. a service name from the catalog → `serviceUUIDs`. Matching is word-level, like the Broker's own `serviceName`
 *    index: every typed word must be a whole word of the name, case-insensitive (`copy` and `media info` match,
 *    `Ra` does not match NxRay), so a caller's first letters are not taken for a service;
 * 2. a status label (Running, Succeeded, Failed, Cancelled) → `statuses`;
 * 3. otherwise the caller: `triggeredByDisplayName startsWith` the text (case-sensitive on the Broker side).
 * Returns an empty object for blank text.
 */
export function searchTextToTenantJobSearchFilters(
	searchText: string,
	services: BrokerService[],
): TenantJobSearchFilters {
	const text = searchText.trim();
	if (!text) return {};
	const lower = text.toLowerCase();

	const typedWords = wordsOf(lower);
	const serviceUUIDs = services
		.filter((service) => {
			const nameWords = wordsOf(service.serviceName.toLowerCase());
			return typedWords.every((word) => nameWords.includes(word));
		})
		.map((service) => service.serviceUUID);
	if (serviceUUIDs.length) return { serviceUUIDs };

	const statuses = SERVICE_LOG_STATUS_QUICK_FILTERS.filter(
		({ value, label }) => value !== 'ALL' && label.toLowerCase() === lower,
	).map(({ value }) => value as TenantJobSearchStatus);
	if (statuses.length) return { statuses };

	return { triggeredByDisplayName: { startsWith: [text] } };
}

function wordsOf(text: string): string[] {
	return text.split(/\s+/).filter(Boolean);
}

/**
 * ANDs two sets of "starts with" prefixes (the Broker ORs the prefixes inside one condition, so two sources
 * cannot simply be concatenated). A name starts with both `a` and `b` only when one is a prefix of the other,
 * and then the longer one is the condition. Either side missing passes the other through; an empty result
 * means nothing can match.
 */
export function intersectPrefixes(a: string[] | undefined, b: string[] | undefined): string[] | undefined {
	if (!a) return b;
	if (!b) return a;
	const longer: string[] = [];
	a.forEach((left) => {
		b.forEach((right) => {
			if (left.startsWith(right)) longer.push(left);
			else if (right.startsWith(left)) longer.push(right);
		});
	});
	return Array.from(new Set(longer));
}
